import { useState, useRef, useEffect } from 'react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger, PopoverAnchor } from "./ui/popover";
import { cn } from "./ui/utils";
import {
  searchAddress,
  getPlaceDetails,
  type PlacePrediction,
} from "../../lib/places";

type Prediction = PlacePrediction;

// Structured details we surface to consumers when a prediction is
// selected. Optional fields stay undefined if the geocoder doesn't
// return them. (Shape kept stable across geocoder migrations.)
export type SelectedPlace = {
  description: string;
  place_id: string;
  latitude?: number;
  longitude?: number;
  postcode?: string;
  city?: string;
  country_code?: string;
};

interface LocationAutocompleteProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onPlaceSelected?: (place: SelectedPlace) => void;
  error?: boolean;
  errorMessage?: string;
  className?: string;
  containerHeight?: string;
  icon?: string;
  placeholder?: string;
  required?: boolean;
}

export function LocationAutocomplete({
  label,
  value,
  onChange,
  onPlaceSelected,
  error = false,
  errorMessage = '',
  className = '',
  containerHeight = 'h-[70px]',
  icon,
  placeholder = 'Start typing your address...',
  required = false,
}: LocationAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const hasValue = value.length > 0;
  const isFloating = isFocused || hasValue || open;

  // Google Autocomplete suggestions carry no coordinates, so on select
  // we resolve the picked place to lat/lng + structured address via a
  // Places Details lookup, then emit the full SelectedPlace. If details
  // fail we still emit the textual selection so the form isn't blocked —
  // coordinates just stay undefined.
  const emitPlace = async (prediction: Prediction) => {
    if (!onPlaceSelected) return;
    try {
      const details = await getPlaceDetails(prediction.place_id);
      onPlaceSelected({
        description: prediction.description,
        place_id: prediction.place_id,
        latitude: details.latitude,
        longitude: details.longitude,
        postcode: details.postcode,
        city: details.city,
        country_code: details.country_code,
      });
    } catch {
      onPlaceSelected({
        description: prediction.description,
        place_id: prediction.place_id,
      });
    }
  };

  // Debounce so we don't hammer the geocoder on every keystroke
  // (Nominatim's public endpoint asks for ~1 req/sec); abort the
  // in-flight request when the user keeps typing.
  useEffect(() => {
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (!value.trim()) {
      setPredictions([]);
      return;
    }
    debounceRef.current = window.setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      searchAddress(value, controller.signal)
        .then((results) => {
          setPredictions(results);
          setLoadError(null);
        })
        .catch((err) => {
          if (err?.name === 'AbortError') return;
          setPredictions([]);
          setLoadError('Could not search addresses. Please try again.');
        });
    }, 400);

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [value]);

  return (
    <div className={cn("w-full relative", className)} ref={containerRef}>
      <Popover open={open && (predictions.length > 0 || !!loadError)} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <div 
            className={cn(
              containerHeight,
              "relative transition-all bg-white rounded-[8px] border",
              error ? "border-red-500" : open ? "border-[#8e2a2f] shadow-[0px_4px_15.3px_0px_rgba(134,58,58,0.1)]" : "border-[#eee]",
              "cursor-text"
            )}
            onClick={() => {
              if (!open) setOpen(true);
            }}
          >
            <input
              type="text"
              value={value}
              onChange={(e) => {
                onChange(e.target.value);
                if (!open) setOpen(true);
              }}
              onFocus={() => {
                setIsFocused(true);
                setOpen(true);
              }}
              onBlur={() => {
                setIsFocused(false);
                // Delay closing to allow clicking item
                setTimeout(() => setOpen(false), 200);
              }}
              placeholder={isFloating ? placeholder : ''}
              className={cn(
                "w-full h-full bg-transparent border-none outline-none px-5 pt-7 pb-2",
                "font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px] tracking-[-0.1px]",
                icon ? "pr-12" : ""
              )}
              style={{ fontVariationSettings: "'opsz' 14" }}
            />
            <label
              className={cn(
                "absolute left-5 font-['DM_Sans',sans-serif] tracking-[-0.1px] pointer-events-none transition-all duration-200",
                error ? "text-red-500" : "text-[#908e8e]",
                isFloating
                  ? "top-[10px] text-[10px] font-normal leading-[18px]"
                  : "top-1/2 -translate-y-1/2 text-[14px] font-bold leading-[21px]"
              )}
              style={{ fontVariationSettings: "'opsz' 14" }}
            >
              {label}
              {required ? (
                <span className="text-[#dc2626] ml-0.5" aria-hidden="true">
                  *
                </span>
              ) : null}
            </label>
            {icon && (
              <span 
                className={cn(
                  "absolute right-5 top-1/2 -translate-y-1/2 material-icons !w-[18px] !h-[18px] !text-[18px] flex items-center justify-center transition-colors pointer-events-none",
                  error ? "text-red-500" : hasValue ? "text-[#3a3335]" : "text-[#908e8e]"
                )}
              >
                {icon}
              </span>
            )}
          </div>
        </PopoverAnchor>
        <PopoverContent 
          className="p-0 w-full min-w-[var(--radix-popover-trigger-width)]" 
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Command className="w-full" shouldFilter={false}>
            <CommandList className="max-h-[300px]">
              {loadError ? (
                <CommandEmpty className="px-4 py-3 text-[13px] text-[#dc2626]">
                  {loadError}
                </CommandEmpty>
              ) : predictions.length === 0 ? (
                <CommandEmpty className="px-4 py-3 text-[13px] text-[#908e8e]">
                  No matches in the UK.
                </CommandEmpty>
              ) : (
                <CommandGroup>
                  {predictions.map((p) => (
                    <CommandItem
                      key={p.place_id}
                      value={p.description}
                      onSelect={() => {
                        onChange(p.description);
                        emitPlace(p);
                        setPredictions([]);
                        setOpen(false);
                      }}
                      className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-[#f2eee2]/50"
                    >
                      <span className="material-icons text-[#908e8e] !w-[18px] !h-[18px] !text-[18px] flex items-center justify-center mt-[2px]">place</span>
                      <span className="flex flex-col">
                        <span className="font-['DM_Sans',sans-serif] text-[14px] font-bold text-[#3a3335]">
                          {p.main_text ?? p.description}
                        </span>
                        {p.secondary_text && (
                          <span className="font-['DM_Sans',sans-serif] text-[12px] text-[#908e8e]">
                            {p.secondary_text}
                          </span>
                        )}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {error && errorMessage && (
        <p className="mt-1 text-red-500 text-[12px] font-['DM_Sans',sans-serif] tracking-[-0.1px]" style={{ fontVariationSettings: "'opsz' 14" }}>
          {errorMessage}
        </p>
      )}
    </div>
  );
}
